import { Module } from 'vuex';
import { isEqual, get } from 'lodash';
import Vue from 'vue';
import { env } from '@hb/mbridge';
import {
  requestCategoryList,
  requestSkuList,
  ReqRequestSkuList,
  requestSkuDetail,
  ReqRequestSkuDetail,
  CombiProduct,
  isHarmonyApp,
} from '../../apis/ticketApis';
import { MarketingWord, Package } from '../../../constants/bike';
import nativeMgr from '../../../common/native/native';
import { isBoComTinyApp } from '../../../common/env/env';
import logger from '../../../common/utils/logger';
import type { AppState } from '../types';
import { getStorageItem, setStorageItem } from '../../../common/utils/storage';

/**
 * 类目数据
 */
export type CategoryData = {
  categoryName: string
  categoryId: string
}

/**
 * 商品权益简要数据
 */
export type EquityBrief = {
  /** 批次号 */
  virtualId: string;
  /** 名称 */
  virtualName: string;
  /** 数量 */
  virtualNum: number;
  /** 业务线 */
  businessType: string;
  /** 1显示，0不显示 */
  needShowNum: number;
  /** 权益类型 */
  propertyType: number;
}

/** 已知权益类型 */
export enum EquityType {
  /** 优惠券 */
  COUPON = 1,
  /** 骑行卡 */
  RIDE_CARD = 2,
  /** 超级会员卡 */
  SUPER_MEMBER_CARD = 15,
  /** 权益卡 */
  EQUITY_CARD = 17,
}

/** 优惠券权益类型 */
export enum EquityCouponType {
  /** 返赠 */
  GIFT,
  /** 金额优惠——无门槛立减 */
  AMOUNT,
  /** 折扣优惠 */
  DISCOUNT,
  /** 满减优惠 */
  FULL_AMOUNT,
  /** 免单优惠 */
  FREE,
  /** 成单返现 */
  CASH,
  /** 满折优惠 */
  FULL_DISCOUNT,
  /** 阶梯减 */
  LADDER_SUB,
  /** 阶梯折 */
  LADDER_DISCOUNT,
}

interface CommonEquityDetail {
  /** 权益类型 */
  virtualType: EquityType;
  /** 批次号 */
  virtualId: string;
  /** 名称 */
  virtualName: string;
  /** 数量 */
  virtualNum: number;
  /** 业务线 */
  businessType: string;
  /** 有效期计算方式 0-指定日期区间 1-有效天数 */
  ruleType: number;
  /** 有效期开始时间 */
  startTime: string;
  /** 有效期开始时间戳 */
  startTimeDate: number;
  /** 有效期结束时间 */
  endTime: string;
  /** 有效期结束时间戳 */
  endTimeDate: number;
  /** 有效天数 */
  validDays: number;
  /** 1显示，0不显示 */
  needShowNum: number;
  worth: number
  /** 折扣 */
  discount: number;
  maxDiscountAmount?: number;
  couponThreshold?: number;
  effectDays?: number;
  commodityDesc?: string;
  /** 阶梯 */
  promoInterval?: PromoIntervalItemType
}

export interface PromoIntervalItemType{
    couponType: EquityCouponType;
    promoInfoList: {
      couponThreshold: number;
      value: number;
    }[]
  }

export interface CouponEquityDetail extends CommonEquityDetail {
  virtualType: EquityType.COUPON;
  /** 当virtualType为1时，会有couponType类型 */
  couponType: EquityCouponType;
  /** 权益类型 */
  propertyType: number;
}

interface OthersEquityDetail extends CommonEquityDetail {
  virtualType: Exclude<EquityType, EquityType.COUPON>;
  /** 权益类型 */
  propertyType: number;
}

/** 商品权益详细数据 */
export type Equity = CouponEquityDetail | OthersEquityDetail

/** 订单商品权益状态 */
export enum OrderShowCouponStatus {
  /** 无可用券 */
  NONE,
  /** 使用中 */
  USE,
  /** 已用完 */
  USED_UP,
  /** 全部过期 */
  OUTDATED,
  /** 已退款 */
  REFUNDED = 4,
}

/** 订单商品权益详细数据 */
export type OrderEquity = Equity & {
  /** 券状态 */
  orderShowCouponStatus: OrderShowCouponStatus;
  /** 是否APP专享 */
  appOnly: boolean;
  /** 剩余可用张数 */
  remainNum: number;
  /** 已使用张数 */
  usedNum: number;
  /** 已过期张数 */
  expiredNum: number;
  /** 类型 */
  couponType: number
  /** 阶梯 */
  promoInterval: any;
  /** 标识第三方类型 */
  propertyType: any;
  /** 图标地址 */
  iconUrl: any;
  /** 是否免费加增 */
  isFreeGranted: boolean;
}

export type IActivity = {
  activityCode: string,
  createTime: number,
  dayLimitNum: number,
  effectiveEndTime: number,
  effectiveStartTime: number,
  guid: string,
  itemId: string,
  limitNum: number,
  ruleId: string,
  saleCityFlag: number,
  saleCitys: string[],
  salePrice: number,
  isDeleted: number,
  saleStock: number,
  skuId: string,
  timeLimitDesc: string,
  timeLimitFlag: number,
  updateTime: number,
  useStatus: number;
  currTime: number
}

export interface IActivityInfo {
  guid: string; // 主键ID
  itemId: string; // 商品ID
  skuId: string; // skuId
  ruleId: string; // 规则ID
  activityCode: string; // 活动code
  effectiveStartTime: number; // 生效开始时间
  effectiveEndTime: number; // 结束时间
  salePrice: number; // 活动价
  saleStock: number; // 活动总库存
  saleNum: number; // 可售库存
}

/** 优惠信息项（promotionInfos / optionalPromotions / disabledPromotions 共用） */
export type SkuPromoInfo = {
  /** 券ID */
  promotionId: string;
  /** 券名称 */
  promotionName: string;
  /** 卡券商城优惠类型 1-营销优惠 3-营销活动 4-新奖励金 */
  couponPromoType: number;
  /** 优惠金额 */
  worth: number;
  /** 可用积分数量 */
  points: number;
}

/**
 * 商品数据
 */
export type SkuData = {
  /** spuId编码 */
  spuId: string;
  /** skuId编码 */
  skuId: string;
  /** 商品名称 */
  goodsName: string;
  /** 售价 */
  salePrice: number;
  /** 原价 */
  originalPrice: number;
  /** 优惠后价格（服务端计算） */
  offerPrice?: number;
  /** 可售数量 */
  saleNum: number;
  /** 商品简介 */
  shortRemark: string;
  /** 商品权益列表 */
  goodsEquitys: Equity[];
  /** 归属分类 */
  categoryId: string;
  /** 归属分类名称 */
  categoryName: string;
  /** 展示分类列表 */
  categoryIds: string[];
  /** 使用须知 */
  useNotice: string;
  /** 列表展示图片 */
  listPicUrl: string;
  /** 商品图片 */
  picUrl: string;
  /** 是否可购买。1是 0否 */
  canPurchase: number;
  /** 活动信息 */
  activityInfo?: IActivity;
  activityUrl?: string;
  groupPrice: number;
  /** 是否算法定价 1, 0 */
  algorithmPriceFlag: string;
  isSuperUser?: boolean;
  participatedNum?: number;
  /** 是否高亮置顶 */
  highlight?: boolean;
  /** 成本价格 */
  costPrice: number;
  /** 两轮卡 */
  twoWheelCard?: Package & {
    /** 渠道码 */
    channelCode: number;
  };
  // 限购数量
  limitNum: number;

  goodsLimit: number;

  /** 优惠信息 */
  promotionInfos?: SkuPromoInfo[];
  // 特殊支付方案标签
  specialPaymentId?: string,
  specialPaymentName?: string,
  promotionPaymentContent?: string,
  // 营销标签
  marketingTagName?: string,
  // 用于标识商品是否为抖音包月商品
  monthlyGoods?: boolean;
  // 包月签约渠道 (alipay: 支付宝, douyin: 抖音)
  monthlyContractChannel?: string;
  // 用于标识商品是否为免密签约商品
  passwordFreeGoods?: boolean;
  // 免密签约渠道 (alipay: 支付宝, douyin: 抖音)
  passwordFreeContractChannel?: string;
  // 秒杀商品
  effectiveActivityInfo?: IActivityInfo;
  // 拼团商品
  collageGoods?: number;
  // 拼团链接
  collageLink?: string;
  // 是否过期自动退
  canAutoRefund?: boolean;
  // 商品标签
  goodsTags?: string[];
}

/** 签约支付信息 */
export type SignPayInfoType = {
  // 支付收款路由
  chargeType: number, // 1308
  // 卡券包月下单场景
  couponMonthlyScene?: string, // "DOUYIN_MONTHLY_PROPERTY" , "ALIPAY_MONTHLY_PROPERTY"
  // 卡券普通免密下单场景
  passwordFreeScene?: string, // "DOUYIN_PASSWORD_PROPERTY" 目前只支持抖音免密支付
  // 查询是否签约过的请求所需场景（支付定义）
  sceneList: string[],
  // 签约渠道
  signChannel: string, // "douyinTrust"
  // 服务端使用字段
  agreementScene?: number, // 88
}

/**
 * 商品详情数据
 */
export type SkuDetailData = Omit<SkuData, 'goodsEquitys'> & {
  /** 商品权益列表 */
  goodsEquitys: Equity[];
  /** 归属分类名称 */
  categoryName: string;
  extraInfo?: {
    businessId: number
  }
  superUserDiscountDTO?: {
    points: number
    amount: number
  }
  /** 优惠信息 */
  promotionInfos?: SkuPromoInfo[];
  /** 可选优惠（用户可手动勾选的优惠，如奖励金） */
  optionalPromotions?: SkuPromoInfo[];
  /** 禁用优惠 */
  disabledPromotions?: SkuPromoInfo[];
  /** 最高省 */
  maxDiscountAmount: number;
  promoDescription: string;
  usableRange: string;
  goodsTags: string[];
  canAutoRefund: boolean;
  // 秒杀商品
  effectiveActivityInfo?: IActivityInfo;
  marketingWords?: MarketingWord[];
  // 支付信息对象（普通免密和包月场景）
  payInfo?: SignPayInfoType;
}

/**
 * 待支付订单信息
 */
export type PayOrderInfo = {
  businessType: string
  businessScene: string
  /** 支付金额 */
  payMoney: string;
  /** 支付编号 */
  payOrderId: string;
  /** 订单编号 */
  billNo: string;
  /** 微信openId */
  openId: string | null;
}

/** 待支付信息 */
export type PayInfo = {
  /** 多笔支付订单 */
  payOrderInfo: PayOrderInfo | PayOrderInfo[]
  /** 业务类型 */
  categoryId: string;
  orderSource?: number;
  activityCode?: string;
  /** 查看订单详情要跳转的url 目前拼团使用的字段，用来跳到拼团活动页面 */
  activityUrl?: string;
  skuData: Pick<SkuData, 'categoryId' | 'spuId' | 'skuId'>;
  combiProduct?: CombiProduct;
  svipAmount?: number;
  /** 是否超值推荐 */
  highRecm?: string;
}

/** 支付结果信息 */
export type PayResult = PayInfo & {
  /** 支付结果 */
  result: string;
}

export type ISubsidyActivityInfo = {
  businessId: number;
  channelCode: number;
}

export type TicketTabData = {
  /** 是否获取过该数据 */
  isGeted: boolean;
  /** 是否正在loading (给list用的) */
  isLoading: boolean;
  /** 给数据用的 */
  isDataLoading: boolean;
  /** 是否第一页数据load错误 */
  isFirstPageLoadErr: boolean;
  /** 数据加载是否出现错误 */
  isPageLoadErr: boolean;
  /** 第一页数据成功回来 */
  isFirstPageDataOk: boolean;
  list: SkuData[];
  hasMore: boolean;
  curPage: number;
  subsidy: {
    hasSubsidy: boolean,
    subsidyActivityInfos: ISubsidyActivityInfo[];
  };
  /** 接口返回的traceId */
  traceId?: string;
}

export type TicketState = {
  /** 限购商品列表页面 */
  limitList: any[];
  /** 类目列表 */
  categoryList: CategoryData[];
  /** 商品列表数据 */
  skuDatas: Record<string, TicketTabData>;
  /** 当前展示的详情页面 */
  curSku: SkuDetailData | null;
  /** 支付情况信息 */
  payInfo: PayInfo | null;
  /** 超值推荐模块数据 */
  greatValueModule: SkuData[];
  /** 超值推荐traceId */
  greatValueTraceId: string;
}

// initial state
const cacheList = getStorageItem<CategoryData[]>('categoryList');
let categoryList: CategoryData[] = [];

if (cacheList && Array.isArray(cacheList)) {
  categoryList = cacheList;
}

// 清理旧版无前缀的 categoryList key（迁移到新 storage 后不再需要）
try {
  if (window.localStorage.getItem('categoryList')) {
    window.localStorage.removeItem('categoryList');
  }
} catch { /* noop */ }

type StateModule = Module<TicketState, AppState>

const initState: StateModule['state'] = {
  categoryList,
  limitList: [],
  skuDatas: {},
  curSku: null,
  payInfo: null,
  greatValueModule: [],
  greatValueTraceId: '',
};

/** 是否初始化了skuDatas */
let isInitSkuDatas = false;
/** 第一次请求的列表数据 */
let tempData: {
  categoryId: string;
  ticketTabData: TicketTabData;
} | null = null;

/** 初始数据 */
const TicketTabInitData: TicketTabData = {
  isGeted: false,
  isLoading: false,
  isDataLoading: false,
  isPageLoadErr: false,
  isFirstPageLoadErr: false,
  isFirstPageDataOk: false,
  list: [],
  hasMore: true,
  curPage: 1,
  subsidy: {
    hasSubsidy: false,
    subsidyActivityInfos: [],
  },
};

const actions: StateModule['actions'] = {
  /**
   * 请求类目列表
   */
  actionRequestCategoryList: async ({
    commit,
    state,
    // dispatch,
  }) => {
    try {
      const res: CategoryData[] = await requestCategoryList();

      // const groupCategory: CategoryData = {
      //   categoryName: '拼团',
      //   categoryId: 'groupon',
      // }

      // 将拼团插入
      // res.splice(1, 0, groupCategory)
      // 如果和本地缓存一致就不需要发送 action
      if (isEqual(res, state.categoryList)) {
        return false;
      }

      setStorageItem({ key: 'categoryList', val: res, expireIn: 1 });
      commit('setDatas', {
        key: 'categoryList',
        value: res,
      });
      // dispatch('actionInitSkuList');
      return true;
    } catch (error) {
      // 请求失败时，保持原有数据不变（错误日志已在 API 层记录）
      logger('[bikeMall] ❌ 求类目请求失败:', error);
      return false;
    }
  },

  /**
   * 初始化商品列表数据
   */
  actionInitSkuList: ({ commit, state }) => {
    const catList: CategoryData[] = state.categoryList;
    if (!catList) {
      return;
    }

    for (let i = 0; i < catList.length; ++i) {
      commit('setSkuDatas', {
        categoryId: catList[i].categoryId,
        ticketTabData: TicketTabInitData,
      });
    }

    if (tempData) {
      commit('setSkuDatas', tempData);
      tempData = null;
    }

    isInitSkuDatas = true;
  },

  /**
   * 请求优惠券列表接口
   */
  actionRequestSkuList: async (
    { commit, state },
    {
      categoryId,
      pageSize = 12,
      pageNum,
      isFirstReq = false,
      needGreatValue = false,
      ...otherParams
    }: {
      categoryId: string;
      pageNum: number;
      pageSize: number,
      isForceUpdate: boolean,
      isFirstReq: boolean,
      needGreatValue?: boolean,
    },
  ) => {
    const ticketTabData: TicketTabData = state.skuDatas[categoryId];
    const finalData: TicketTabData = {
      ...TicketTabInitData,
      ...ticketTabData,
    };

    if (pageNum === 1) { // 如果是第一页则清空list
      finalData.list = [];
    }

    const getChannelId = () => {
      if (env.isEasyBikeApp) return 1;
      if (env.isAliMP) return 2;
      if (env.isWeixin || env.isWechatMini) return 4;
      if (env.isHelloHitchApp) return 5; // 顺风车独立app
      if ((env as any).isTTMP) return 6;
      if (env.isHelloRentApp) return 7; // 租车独立app
      if (isHarmonyApp) return 8;
      if (isBoComTinyApp) return 9; // 交银小程序
      return 3;
    };

    const req: ReqRequestSkuList = {
      // 1 App, 2 支付宝小程序, 3 其他h5环境
      // eslint-disable-next-line
      channelId: getChannelId(),
      categoryId,
      pageNum,
      pageSize,
      // * 以下是两轮骑行卡需要参数
      version: (await nativeMgr.getDeviceInfo()).version,
      _uuid: window.$$user?.userGuid,
      needGreatValue,
      removeFromTopSkuIds: state.greatValueModule.length > 0
        ? state.greatValueModule.map((item) => item.skuId)
        : undefined,
      ...otherParams,
    };

    let resList: SkuData[] = [];
    let res: {
      pageCount: number // 总页数
      recordsCount: number // 总记录数
      pageSize: number // 每页数量
      pageNum: number // 当前页号
      data: SkuData[],
      greatValueModule?: SkuData[], // 超值推荐模块数据
      subsidy: {
        hasSubsidy: boolean,
        subsidyActivityInfos: ISubsidyActivityInfo[],
      },
      traceId?: string
    } | null = null;

    try {
      if (pageNum === 1) { // 如果是第一页
        finalData.isFirstPageDataOk = false;
      }

      finalData.isFirstPageLoadErr = false;
      finalData.isDataLoading = true;
      commit('setSkuDatas', {
        categoryId,
        ticketTabData: finalData,
      });

      res = await requestSkuList(req);

      // 处理超值推荐模块数据（只在needGreatValue为true时更新）
      if (needGreatValue && res?.greatValueModule && res.greatValueModule.length > 0) {
        // 只取前两个商品
        const greatValueList = res.greatValueModule.slice(0, 2);
        commit('setDatas', {
          key: 'greatValueModule',
          value: greatValueList,
        });
        commit('setDatas', {
          key: 'greatValueTraceId',
          value: res?.traceId || '',
        });
      }

      finalData.subsidy = res?.subsidy || {
        hasSubsidy: false,
        subsidyActivityInfos: [],
      };
      finalData.traceId = res?.traceId || '';
      // resList = res.data || []
      resList = res?.data.filter((i) => {
        // 如果库存 0 则不过滤
        const r = state.limitList.find((s) => i.skuId === s.skuId && get(s, 'activityInfo.saleStock') && s.started);
        return !r;
      }) || [];

      finalData.list = finalData.list.concat(resList);
      finalData.isGeted = true;
      finalData.curPage = pageNum;

      const recordsCount = res?.recordsCount || 0; // 服务端可能未设置recordsCount

      if (pageNum === 1) {
        finalData.isFirstPageDataOk = true;
      }

      if (finalData.list.length < recordsCount) {
        finalData.hasMore = true;
      } else {
        finalData.hasMore = false;
      }
      window._loading = false;
    } catch (e) {
      finalData.isPageLoadErr = true;

      if (pageNum <= 1) { // 当时第一页数据时，网络出错，则认为没有数据了
        finalData.isFirstPageLoadErr = true;
        finalData.hasMore = false;
        commit('setSkuDatas', {
          categoryId,
          ticketTabData: finalData,
        });
      } else { // 如果请求的不是第一页数据
        if (!finalData.list || finalData.list.length <= 0) {
          finalData.hasMore = false;
          finalData.isFirstPageLoadErr = true;
          commit('setSkuDatas', {
            categoryId,
            ticketTabData: finalData,
          });
        }
      }
      throw (e);
    } finally {
      // isRequestSkuList = false
      finalData.isLoading = false;
      finalData.isDataLoading = false;
    }

    if (isFirstReq && !isInitSkuDatas) {
      tempData = {
        categoryId,
        ticketTabData: finalData,
      };
    } else {
      commit('setSkuDatas', {
        categoryId,
        ticketTabData: finalData,
      });
    }

    window._loading = false;
  },
  actionResetGoodsList({ commit, state }) {
    const catList: CategoryData[] = state.categoryList;
    if (!catList) {
      return;
    }
    for (let i = 0; i < catList.length; ++i) {
      commit('setSkuDatas', {
        categoryId: catList[i].categoryId,
        ticketTabData: TicketTabInitData,
      });
    }
  },
  /**
   * 请求当前优惠券详情
   */
  actionRequestCurSkuDetail: async ({ commit }, { spuId, algorithmPriceFlag }: {
    skuId: number;
    spuId: number;
    algorithmPriceFlag: string;
  }) => {
    commit('setDatas', {
      key: 'curSku',
      value: {},
    });
    // 链接上的参数可能有问题，如果带有？或者&，则进行截取
    let finaleSpuId: any = spuId;
    if (finaleSpuId.indexOf('?') >= 0) {
      finaleSpuId = finaleSpuId.substr(0, finaleSpuId.indexOf('?'));
    }
    if (finaleSpuId.indexOf('&') >= 0) {
      finaleSpuId = finaleSpuId.substr(0, finaleSpuId.indexOf('&'));
    }
    const req: ReqRequestSkuDetail = {
      spuId: finaleSpuId,
      algorithmPriceFlag,
    };
    try {
      const res = await requestSkuDetail(req);
      commit('setDatas', {
        key: 'curSku',
        value: res,
      });
    } catch (error) {
      commit('setDatas', {
        key: 'curSku',
        value: {},
      });
    }
  },

  /**
   * 设置支付结果
   */
  actionSetPayInfo: ({ commit }, payInfo: PayInfo) => {
    if (!payInfo) {
      return;
    }

    commit('setDatas', {
      key: 'payInfo',
      value: payInfo,
    });
  },
  actionSetLimitList: ({ commit }, limitList: any[]) => {
    commit('setLimitList', limitList);
  },
};

const mutations: StateModule['mutations'] = {
  setDatas: (state, cellData: { key: string, value: any }) => {
    if (!cellData || !cellData.key) {
      return;
    }

    const { key } = cellData;
    state[key] = cellData.value;
  },
  setSkuDatas: (state, data: { categoryId: string; ticketTabData: TicketTabData }) => {
    if (!data) {
      return;
    }

    Vue.set(state.skuDatas, data.categoryId, data.ticketTabData);
  },
  setLimitList: (state, data: any[]) => {
    if (!data) {
      return;
    }
    state.limitList = data;
  },
  /** 批量按 spuId 更新商品列表中对应项的价格信息（购买后刷新用） */
  updateSkuPromoInfo: (state, promoData: {
    spuId: string;
    skuId: string;
    salePrice: string;
    originalPrice: string;
    offerPrice?: number;
    promotionInfos?: SkuPromoInfo[];
    optionalPromotions?: SkuPromoInfo[];
    disabledPromotions?: SkuPromoInfo[];
    [key: string]: unknown;
  }[]) => {
    if (!promoData?.length) return;
    // 构建 spuId → promo 数据的映射（过滤无效 spuId，防止 undefined 匹配错乱）
    const promoMap = new Map(
      promoData
        .filter((item) => item.spuId && String(item.spuId) !== 'undefined')
        .map((item) => [String(item.spuId), item]),
    );
    // 遍历所有分类的商品列表，按 spuId 匹配全量更新
    Object.values(state.skuDatas).forEach((tabData: TicketTabData) => {
      tabData.list?.forEach((skuItem) => {
        const promo = promoMap.get(String(skuItem.spuId));
        if (promo) {
          // 接口未返回的字段主动设置默认值，避免残留旧数据
          const normalized = {
            ...promo,
            offerPrice: promo.offerPrice ?? null,
            promotionInfos: promo.promotionInfos ?? [],
            optionalPromotions: promo.optionalPromotions ?? [],
            disabledPromotions: promo.disabledPromotions ?? [],
          };
          Object.assign(skuItem, normalized);
        }
      });
    });
    // 同步更新超值精选模块中的商品价格
    // 使用 Vue.set 按索引替换，确保新增属性也能触发响应式更新（Vue 2 限制）
    state.greatValueModule?.forEach((skuItem, index) => {
      const promo = promoMap.get(String(skuItem.spuId));
      if (promo) {
        const normalized = {
          ...promo,
          offerPrice: promo.offerPrice ?? null,
          promotionInfos: promo.promotionInfos ?? [],
          optionalPromotions: promo.optionalPromotions ?? [],
          disabledPromotions: promo.disabledPromotions ?? [],
        };
        Vue.set(state.greatValueModule, index, { ...skuItem, ...normalized });
      }
    });
  },
};

export default {
  namespaced: true,
  state: initState,
  actions,
  mutations,
};
